/*  CourseView — Learner-side viewer for a DB-driven course.
    ────────────────────────────────────────────────────────────────────────
    Route: /doctor/academy/courses/:courseId  (opened in a new tab from
    the LearningAcademyCatalogue).

    iter100.113t — Massive UX pass:
        • Sticky bottom navigation bar with Prev / Next + smart CTA
        • Position indicator ("Lesson 3 of 7 · Module 2 of 4")
        • Course-progress bar in the footer
        • Explicit "Mark as complete" (auto-mark on view is still there but
          the checkbox lets the doctor take control)
        • Notes drawer (private per-lesson notes, auto-saved)
        • Bookmark toggle
        • Rate lesson (thumbs up / down)
        • Ask CoE Support (question routed to a queue)
        • Share lesson URL
        • Auto-advance countdown on lesson complete
        • Module completion celebration toast
        • Study streak chip + estimated time to certificate in header
        • Keyboard shortcuts: ← prev, → next, Esc back to catalogue
        • Auto-scroll to top on lesson change
        • Print / download transcript (text lessons only)
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, VideoCamera, Article, FileArrowUp, Question, House, SpeakerHigh,
    CheckCircle, CaretRight, CaretDown, DownloadSimple, Play,
    Books, Lock, ClockCounterClockwise, BookmarkSimple, NotePencil,
    ThumbsUp, ThumbsDown, ChatCircleDots, ShareNetwork, Fire, Clock,
    Trophy, Printer, X, FileText, Paperclip, UploadSimple, Trash,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import RichTextView, { expandEmbeds } from "@/components/creator/RichTextView";
import PaymentPendingDialog from "@/components/doctor/PaymentPendingDialog";
import NarrationPlayer from "@/components/doctor/NarrationPlayer";
import RetakeCourseDialog from "@/components/doctor/RetakeCourseDialog";
import { useAuth } from "@/context/AuthContext";


const LESSON_TYPE_META = {
    video:    { label: "Video",     Icon: VideoCamera },
    text:     { label: "Text",      Icon: Article },
    narrated: { label: "Narrated",  Icon: SpeakerHigh },
    file:     { label: "File",      Icon: FileArrowUp },
    quiz:     { label: "Quiz",      Icon: Question },
};


// Very small helper — turns a bunch of URL shapes into an embeddable URL.
function toEmbedUrl(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, "");
        // YouTube
        if (host === "youtube.com" || host === "m.youtube.com") {
            const vid = u.searchParams.get("v");
            if (vid) return `https://www.youtube.com/embed/${vid}`;
        }
        if (host === "youtu.be") {
            const vid = u.pathname.replace(/^\//, "");
            if (vid) return `https://www.youtube.com/embed/${vid}`;
        }
        // Vimeo
        if (host === "vimeo.com") {
            const vid = u.pathname.replace(/^\//, "").split("/")[0];
            if (vid) return `https://player.vimeo.com/video/${vid}`;
        }
        // Loom
        if (host === "loom.com" && u.pathname.startsWith("/share/")) {
            return url.replace("/share/", "/embed/");
        }
        // Google Drive share link
        if (host === "drive.google.com" && u.pathname.includes("/file/d/")) {
            return url.replace(/\/view.*$/, "/preview");
        }
    } catch { /* not a URL */ }
    return url;
}


export default function CourseView() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [progress, setProgress] = useState(null);         // /courses/{id}/my-progress
    const [loading, setLoading] = useState(true);
    const [activeLessonId, setActiveLessonId] = useState(null);
    // iter100.157nx — "Take Final Exam" CTA from the sticky footer opens the
    // Final Exam VIEW in the main content area (not a sidebar drawer).
    // iter100.157nx2 — Final Exam now lives in the main content column, side
    // by side with lessons in the same slot. `activeFinalExam` toggles which
    // renders in the main area. `finalExamOpenTrigger` is a counter used to
    // ask FinalExamView to auto-start a fresh session (footer CTA + sidebar click).
    const [activeFinalExam, setActiveFinalExam] = useState(false);
    const [finalExamOpenTrigger, setFinalExamOpenTrigger] = useState(0);
    // iter100.157q — Course-complete celebration overlay state.
    const [courseCompleteResult, setCourseCompleteResult] = useState(null);
    // iter100.157r — Hybrid retake — reset the whole course (keeps existing cert).
    const [resetting, setResetting] = useState(false);
    const [retakeDlgOpen, setRetakeDlgOpen] = useState(false);
    const openFinalExam = useCallback(() => {
        setActiveFinalExam(true);
        setActiveLessonId(null);
        setFinalExamOpenTrigger((n) => n + 1);
    }, []);
    // Whenever the doctor clicks a lesson, kick us out of the exam view.
    const selectLesson = useCallback((lessonId) => {
        setActiveFinalExam(false);
        setActiveLessonId(lessonId);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [contentResp, progResp] = await Promise.all([
                api.get("/academy/published-courses"),
                api.get(`/academy/courses/${courseId}/my-progress`).catch((e) => ({ err: e })),
            ]);
            const found = (contentResp.data.courses || []).find((c) => c.id === courseId);
            if (!found) throw new Error("Course not found");
            setCourse(found);
            if (progResp.err) {
                toast.error(progResp.err?.response?.data?.detail || "Prerequisites not met.");
                navigate("/doctor/academy");
                return;
            }
            setProgress(progResp.data);
            const firstMod = found.modules?.[0];
            const firstLesson = firstMod?.lessons?.[0];
            if (firstLesson) setActiveLessonId(firstLesson.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Couldn't load course.");
            navigate("/doctor/academy");
        } finally {
            setLoading(false);
        }
    }, [courseId, navigate]);

    // Reload progress after a lesson complete / quiz submit / final exam without
    // re-fetching the whole content payload.
    const reloadProgress = useCallback(async () => {
        try {
            const { data } = await api.get(`/academy/courses/${courseId}/my-progress`);
            setProgress(data);
        } catch { /* silent */ }
    }, [courseId]);

    // iter100.157r — Hybrid retake: wipe the enrollment (lesson_progress,
    // quiz attempts, final-exam session) so the doctor can retake the course.
    // Their existing certificate stays in their wallet as proof of the first pass.
    const handleResetCourse = useCallback(async () => {
        setResetting(true);
        try {
            await api.post(`/academy/courses/${courseId}/reset`);
            toast.success("Course reset. Starting fresh from the top.");
            // Reset local UI state.
            setActiveFinalExam(false);
            setCourseCompleteResult(null);
            // iter100.157s — Clear the celebration bookkeeping so completing
            // modules the second time still fires the overlay + toast.
            celebratedRef.current = new Set();
            initialCompletionSyncedRef.current = false;
            setCelebratingModuleId(null);
            // Reload progress + jump to the very first lesson.
            await reloadProgress();
            const firstLesson = course?.modules?.[0]?.lessons?.[0];
            if (firstLesson) setActiveLessonId(firstLesson.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reset the course.");
            throw e;
        } finally {
            setResetting(false);
        }
    }, [courseId, course, reloadProgress]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const prev = document.title;
        if (course) document.title = `${course.title} — Invisique Academy`;
        return () => { document.title = prev; };
    }, [course]);

    const flatLessons = useMemo(() => {
        if (!course) return [];
        const flat = [];
        (course.modules || []).forEach((m) => {
            (m.lessons || []).forEach((l) => flat.push({ ...l, module: m }));
        });
        return flat;
    }, [course]);

    const activeLesson = useMemo(() => flatLessons.find((l) => l.id === activeLessonId) || null, [flatLessons, activeLessonId]);

    // iter100.113t — Prev/Next lookups on the flat lesson array.
    const activeIndex = useMemo(
        () => (activeLesson ? flatLessons.findIndex((l) => l.id === activeLesson.id) : -1),
        [flatLessons, activeLesson],
    );
    const prevLesson = activeIndex > 0 ? flatLessons[activeIndex - 1] : null;
    const nextLesson = activeIndex >= 0 && activeIndex < flatLessons.length - 1
        ? flatLessons[activeIndex + 1]
        : null;

    // Derived state used across footer + header.
    const lessonProgress = progress?.enrollment?.lesson_progress || {};
    const summary = progress?.summary;
    const finalConfig = progress?.final_exam_config || {};
    const cert = progress?.certificate;
    // iter100.157r — Distinguishes a truly-completed enrollment from a
    // freshly-reset one (which still has a `cert` in the wallet). Only the
    // former should show the "Retake Course" topbar button.
    const isTrulyCompleted = progress?.enrollment?.status === "completed";

    /* iter100.113t — Study streak. Count distinct calendar days on which
       the doctor has viewed or completed at least one lesson, going
       back from today until we hit a gap. */
    const streakDays = useMemo(() => computeStreak(lessonProgress), [lessonProgress]);

    /* Estimated time-to-certificate — sums duration of remaining unviewed
       lessons using the course.duration_minutes ÷ total_lessons average
       (courses don't yet carry per-lesson durations). */
    const etaMinutes = useMemo(() => {
        if (!course) return 0;
        const totalMin = Number(course.duration_minutes || 0);
        const totalLessons = Number(course.total_lessons || flatLessons.length || 1);
        if (!totalMin) return 0;
        const perLesson = totalMin / totalLessons;
        const remaining = flatLessons.filter((l) => !lessonProgress[l.id]?.viewed_at).length;
        return Math.round(perLesson * remaining);
    }, [course, flatLessons, lessonProgress]);

    /* iter100.157s — Sequential lesson gating.
       A lesson is locked when ANY preceding lesson in the flat sequence
       is not yet complete (viewed for content lessons; quiz passed for
       quiz lessons). Blocks doctors from skipping ahead. */
    const lessonLockedMap = useMemo(() => {
        const out = {};
        for (let i = 0; i < flatLessons.length; i++) {
            const l = flatLessons[i];
            if (i === 0) { out[l.id] = false; continue; }
            const prev = flatLessons[i - 1];
            const pProg = lessonProgress[prev.id] || {};
            const prevDone = !!pProg.viewed_at && (prev.type !== "quiz" || pProg.quiz?.passed);
            const prevLocked = out[prev.id];
            out[l.id] = prevLocked || !prevDone;
        }
        return out;
    }, [flatLessons, lessonProgress]);

    // Module is "locked" when its first lesson is locked.
    const moduleLockedMap = useMemo(() => {
        const out = {};
        if (!course) return out;
        for (const m of course.modules || []) {
            const first = (m.lessons || [])[0];
            out[m.id] = first ? !!lessonLockedMap[first.id] : false;
        }
        return out;
    }, [course, lessonLockedMap]);

    // Per-module "all lessons + quiz done" state for sidebar badges.
    const moduleCompletionMap = useMemo(() => {
        const out = {};
        if (!course) return out;
        for (const m of course.modules || []) {
            const lessons = m.lessons || [];
            if (!lessons.length) { out[m.id] = false; continue; }
            out[m.id] = lessons.every((l) => {
                const p = lessonProgress[l.id] || {};
                return p.viewed_at && (l.type !== "quiz" || p.quiz?.passed);
            });
        }
        return out;
    }, [course, lessonProgress]);

    // Intercept clicks on locked lessons with a friendly, targeted toast.
    const attemptSelectLesson = useCallback((lessonId) => {
        if (lessonLockedMap[lessonId]) {
            const idx = flatLessons.findIndex((l) => l.id === lessonId);
            const blocker = idx > 0
                ? flatLessons.slice(0, idx).reverse().find((l) => {
                    const p = lessonProgress[l.id] || {};
                    return !p.viewed_at || (l.type === "quiz" && !p.quiz?.passed);
                })
                : null;
            const msg = blocker
                ? blocker.type === "quiz"
                    ? `Pass the quiz "${blocker.title}" first to unlock this lesson.`
                    : `Finish "${blocker.title}" first to unlock this lesson.`
                : "Complete the previous lesson first to unlock this one.";
            toast.error(msg);
            return;
        }
        selectLesson(lessonId);
    }, [lessonLockedMap, flatLessons, lessonProgress, selectLesson]);

    /* iter100.157s — UNIFIED module-complete celebration.
       Previously we ran two overlapping effects — one for the small toast,
       one for the full-viewport overlay — and only the toast reliably fired
       because the overlay effect skipped modules that looked "already done"
       during a fragile initial-sync branch. This single effect now handles
       both notifications with the exact same "did the module JUST flip?"
       check the toast used.

       On first mount we mark every currently-completed module as already
       celebrated (no false-positive on page reload). Every subsequent
       transition from unfinished → complete fires the toast AND raises
       the full-viewport `ModuleCompleteCelebration` overlay. */
    const celebratedRef = useRef(new Set());
    const initialCompletionSyncedRef = useRef(false);
    const [celebratingModuleId, setCelebratingModuleId] = useState(null);
    useEffect(() => {
        if (!course || !progress) return;
        const modules = course.modules || [];

        // Initial sync — silently record which modules are already done so
        // we don't celebrate them on page reload.
        if (!initialCompletionSyncedRef.current) {
            modules.forEach((m) => {
                const allDone = (m.lessons || []).length > 0
                    && (m.lessons || []).every((l) => {
                        const p = lessonProgress[l.id] || {};
                        return p.viewed_at && (l.type !== "quiz" || p.quiz?.passed);
                    });
                if (allDone) celebratedRef.current.add(m.id);
            });
            initialCompletionSyncedRef.current = true;
            return;
        }

        // Transition detection — find the first module that JUST flipped.
        for (let mIdx = 0; mIdx < modules.length; mIdx++) {
            const m = modules[mIdx];
            const allDone = (m.lessons || []).length > 0
                && (m.lessons || []).every((l) => {
                    const p = lessonProgress[l.id] || {};
                    return p.viewed_at && (l.type !== "quiz" || p.quiz?.passed);
                });
            if (allDone && !celebratedRef.current.has(m.id)) {
                celebratedRef.current.add(m.id);
                const next = modules[mIdx + 1];
                toast.success(
                    next
                        ? `Module ${mIdx + 1} complete! Up next: ${next.title}`
                        : "Final module complete — nearly there!",
                    { duration: 4500 },
                );
                setCelebratingModuleId(m.id);
                break;
            }
        }
    }, [course, progress, lessonProgress]);

    // Auto-scroll to top of main content when the lesson changes.
    const mainScrollRef = useRef(null);
    useEffect(() => {
        if (mainScrollRef.current) {
            mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [activeLessonId]);

    /* iter100.113t — Keyboard shortcuts. ArrowLeft / ArrowRight jump
       between lessons; Esc returns to the catalogue. Skip when the user
       is typing in an input / textarea / contenteditable. */
    useEffect(() => {
        const handler = (e) => {
            const target = e.target;
            const isEditable = target && (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.isContentEditable
            );
            if (isEditable) return;
            if (e.key === "ArrowLeft" && prevLesson) { selectLesson(prevLesson.id); e.preventDefault(); }
            else if (e.key === "ArrowRight" && nextLesson) { selectLesson(nextLesson.id); e.preventDefault(); }
            else if (e.key === "Escape") { navigate("/doctor/academy"); }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [prevLesson, nextLesson, navigate]);

    if (loading || !course) {
        return (
            <div className="min-h-screen p-8 bg-secondary/30">
                <div className="max-w-6xl mx-auto space-y-4">
                    <div className="h-8 w-56 bg-secondary rounded animate-pulse" />
                    <div className="h-96 bg-secondary rounded animate-pulse" />
                </div>
            </div>
        );
    }

    // Paywall gate — the doctor navigated directly to a paid course they haven't
    // purchased. Show a clean gate + auto-open the Contact-CoE dialog.
    if (progress?.payment_required) {
        const rupees = Math.round((progress.pricing?.price_amount_cents || 0) / 100);
        return <PaywallGate course={course} priceRupees={rupees} />;
    }

    return (
        <div className="min-h-screen bg-secondary/30 flex flex-col" data-testid="course-view">
            {/* Topbar */}
            <header className="bg-card border-b border-border sticky top-0 z-30" data-testid="course-view-topbar">
                <div className="px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <Link
                            to="/doctor/academy"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline"
                            data-testid="course-view-back-link"
                        >
                            <ArrowLeft size={11} weight="bold" /> Invisique Academy
                        </Link>
                        <div className="mt-1 flex items-center gap-3 flex-wrap">
                            <h1 className="font-display text-lg font-bold tracking-tight truncate max-w-2xl" data-testid="course-view-title">
                                {course.title}
                            </h1>
                            <div className="hidden sm:flex items-center gap-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                                {course.track && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.track}</span>}
                                {course.phase && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.phase}</span>}
                                {course.duration_minutes ? <span>{course.duration_minutes} min</span> : null}
                                <span>{course.total_modules}m · {course.total_lessons}l</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {streakDays > 0 && (
                            <div
                                className="hidden md:flex items-center gap-1 h-8 px-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-bold"
                                title={`You've studied on ${streakDays} distinct day${streakDays === 1 ? "" : "s"} recently.`}
                                data-testid="course-view-streak-chip"
                            >
                                <Fire size={12} weight="fill" className="text-amber-600" />
                                {streakDays}-day streak
                            </div>
                        )}
                        {!cert && etaMinutes > 0 && (
                            <div
                                className="hidden md:flex items-center gap-1 h-8 px-2.5 rounded-md bg-secondary border border-border text-[11px] font-semibold text-foreground"
                                title="Estimated remaining study time to earn the certificate."
                                data-testid="course-view-eta-chip"
                            >
                                <Clock size={12} weight="duotone" />
                                {formatEta(etaMinutes)} to cert
                            </div>
                        )}
                        {summary && (
                            <div className="hidden sm:flex items-center gap-2 h-8 px-3 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-bold" data-testid="course-view-progress-chip">
                                {summary.percent}% complete
                            </div>
                        )}
                        {cert && (
                            <Button
                                size="sm"
                                onClick={() => downloadCertificateFile(cert.id)}
                                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="course-view-cert-btn"
                            >
                                <Trophy size={12} weight="fill" className="mr-1" /> Certificate
                            </Button>
                        )}
                        {cert && isTrulyCompleted && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRetakeDlgOpen(true)}
                                disabled={resetting}
                                className="h-8 border-slate-300 hover:bg-slate-100 text-slate-700"
                                title="Retake this course from scratch. Your existing certificate stays safe in your wallet."
                                data-testid="course-view-reset-btn"
                            >
                                <ClockCounterClockwise size={12} weight="bold" className="mr-1" />
                                {resetting ? "Resetting…" : "Retake Course"}
                            </Button>
                        )}
                        <Link
                            to="/doctor"
                            className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border bg-secondary text-xs font-semibold no-underline hover:bg-secondary/60 transition-colors"
                            data-testid="course-view-home-btn"
                        >
                            <House size={11} weight="fill" /> Home
                        </Link>
                    </div>
                </div>
            </header>

            {/* iter100.157r — Premium retake confirmation. */}
            <RetakeCourseDialog
                open={retakeDlgOpen}
                onOpenChange={setRetakeDlgOpen}
                courseTitle={course.title}
                onConfirm={handleResetCourse}
            />

            {/* Split view */}
            <div className="flex-1 flex flex-col lg:flex-row min-h-0 pb-24" data-testid="course-view-body">
                {/* Sidebar */}
                <aside className="w-full lg:w-80 shrink-0 border-r border-border bg-card overflow-y-auto max-h-[calc(100vh-4rem)]" data-testid="course-view-sidebar">
                    <div className="p-3">
                        {(course.modules || []).map((m, mIdx) => (
                            <ModuleAccordion
                                key={m.id}
                                module={m}
                                index={mIdx}
                                activeLessonId={activeLessonId}
                                onSelect={attemptSelectLesson}
                                lessonProgress={lessonProgress}
                                lessonLockedMap={lessonLockedMap}
                                moduleLocked={moduleLockedMap[m.id]}
                                moduleComplete={moduleCompletionMap[m.id]}
                            />
                        ))}
                        {finalConfig.enabled && (
                            <FinalExamNav
                                progress={progress}
                                onOpen={openFinalExam}
                                isActive={activeFinalExam}
                            />
                        )}
                        {summary?.case_required && (
                            <CaseSubmissionNav
                                courseId={courseId}
                                progress={progress}
                                onProgressUpdate={reloadProgress}
                            />
                        )}
                    </div>
                </aside>

                {/* Main */}
                <main
                    ref={mainScrollRef}
                    className="flex-1 overflow-y-auto p-6 md:p-10 min-w-0"
                    data-testid="course-view-main"
                >
                    {activeFinalExam ? (
                        <FinalExamView
                            courseId={courseId}
                            progress={progress}
                            onExamPassed={reloadProgress}
                            openTrigger={finalExamOpenTrigger}
                            onCoursePassed={(data) => setCourseCompleteResult(data)}
                        />
                    ) : activeLesson ? (
                        <LessonPlayer
                            lesson={activeLesson}
                            courseId={courseId}
                            lessonProgress={lessonProgress}
                            onProgressUpdate={reloadProgress}
                        />
                    ) : (
                        <div className="text-center text-muted-foreground py-16">
                            <Books size={40} weight="duotone" className="mx-auto mb-3" />
                            <div className="text-sm font-semibold">Pick a lesson to start</div>
                        </div>
                    )}
                </main>
            </div>

            {/* iter100.157p — Module-complete celebration overlay.
                Renders on top of the whole course view (over the main content
                + footer) whenever the doctor finishes the last unfinished
                lesson of a module. */}
            {celebratingModuleId && (
                <ModuleCompleteCelebration
                    course={course}
                    moduleId={celebratingModuleId}
                    lessonProgress={lessonProgress}
                    finalConfig={finalConfig}
                    onContinue={(target) => {
                        setCelebratingModuleId(null);
                        if (target === "final-exam") {
                            openFinalExam();
                        } else if (target?.lessonId) {
                            selectLesson(target.lessonId);
                        }
                    }}
                    onDismiss={() => setCelebratingModuleId(null)}
                />
            )}

            {/* iter100.157q — Course-complete celebration. Fires after the
                doctor passes the Final Exam. Certificate downloads reuse
                the existing PDF endpoint. */}
            {courseCompleteResult && (
                <CourseCompleteCelebration
                    course={course}
                    result={courseCompleteResult}
                    certificate={cert}
                    doctorName={user?.name}
                    onDownload={() => cert?.id && downloadCertificateFile(cert.id)}
                    onDismiss={() => setCourseCompleteResult(null)}
                />
            )}

            {/* iter100.113t — Sticky bottom footer bar. Hidden during the exam view
                because the exam has its own Submit button + no next-lesson. */}
            {activeLesson && !activeFinalExam && (
                <CourseFooter
                    course={course}
                    courseId={courseId}
                    lesson={activeLesson}
                    lessonProgress={lessonProgress}
                    summary={summary}
                    cert={cert}
                    isTrulyCompleted={isTrulyCompleted}
                    prevLesson={prevLesson}
                    nextLesson={nextLesson}
                    activeIndex={activeIndex}
                    total={flatLessons.length}
                    finalConfig={finalConfig}
                    onSelect={selectLesson}
                    onProgressUpdate={reloadProgress}
                    onRequestFinalExam={openFinalExam}
                />
            )}
        </div>
    );
}


async function downloadCertificateFile(certId) {
    try {
        const resp = await api.get(`/academy/certificates/${certId}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        window.open(url, "_blank");
    } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't fetch certificate.");
    }
}


function ModuleAccordion({ module, index, activeLessonId, onSelect, lessonProgress, lessonLockedMap, moduleLocked, moduleComplete }) {
    const hasActive = (module.lessons || []).some((l) => l.id === activeLessonId);
    // Auto-open the first module + the module containing the active lesson,
    // plus any module whose lessons the doctor can currently access
    // (i.e., not the far-future locked ones).
    const [open, setOpen] = useState(index === 0 || hasActive || !moduleLocked);
    return (
        <div className="mb-2" data-testid={`view-module-${module.id}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${
                    open ? "bg-primary/5" : "hover:bg-secondary/40"
                }`}
                data-testid={`view-module-toggle-${module.id}`}
            >
                <div className={`w-6 h-6 shrink-0 rounded-md text-[10px] font-bold tabular-nums grid place-items-center ${
                    moduleComplete
                        ? "bg-emerald-500 text-white"
                        : open ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                }`}>
                    {moduleComplete ? <CheckCircle size={12} weight="fill" /> : (index + 1)}
                </div>
                <div className="flex-1 min-w-0 text-xs font-semibold truncate">{module.title}</div>
                {moduleLocked && (
                    <Lock
                        size={11}
                        weight="fill"
                        className="text-slate-400 shrink-0"
                        data-testid={`view-module-locked-${module.id}`}
                    />
                )}
                {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            </button>
            {open && (
                <div className="ml-5 mt-1 space-y-0.5 border-l border-border pl-2">
                    {(module.lessons || []).map((l, lIdx) => {
                        const meta = LESSON_TYPE_META[l.type] || LESSON_TYPE_META.text;
                        const isActive = l.id === activeLessonId;
                        const prog = lessonProgress[l.id] || {};
                        const viewed = !!prog.viewed_at;
                        const quizPassed = l.type === "quiz" && prog.quiz?.passed;
                        const bookmarked = !!prog.bookmarked;
                        const hasNotes = !!(prog.notes && prog.notes.trim());
                        const locked = !!(lessonLockedMap && lessonLockedMap[l.id]);
                        return (
                            <button
                                key={l.id}
                                type="button"
                                onClick={() => onSelect(l.id)}
                                aria-disabled={locked}
                                className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-left text-[11px] transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary font-semibold"
                                        : locked
                                            ? "text-slate-400 cursor-not-allowed"
                                            : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                                }`}
                                data-testid={`view-lesson-select-${l.id}`}
                            >
                                {locked ? (
                                    <Lock size={11} weight="fill" className="shrink-0 text-slate-400" data-testid={`view-lesson-locked-${l.id}`} />
                                ) : (
                                    <meta.Icon size={11} weight={isActive ? "fill" : "duotone"} className="shrink-0" />
                                )}
                                <span className="flex-1 min-w-0 truncate">{lIdx + 1}. {l.title}</span>
                                {bookmarked && (
                                    <BookmarkSimple
                                        size={10}
                                        weight="fill"
                                        className="text-rose-500 shrink-0"
                                        data-testid={`view-lesson-bookmark-icon-${l.id}`}
                                    />
                                )}
                                {hasNotes && (
                                    <NotePencil
                                        size={10}
                                        weight="fill"
                                        className="text-amber-500 shrink-0"
                                        data-testid={`view-lesson-notes-icon-${l.id}`}
                                    />
                                )}
                                {(viewed || quizPassed) && (
                                    <CheckCircle size={11} weight="fill" className="text-emerald-500 shrink-0" data-testid={`view-lesson-done-${l.id}`} />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/*  CourseCompleteCelebration — iter100.157q
    Fires when the doctor passes the Final Exam. Full-viewport overlay
    grander than the ModuleCompleteCelebration:
      · Gold Trophy in a coloured glow
      · Big score number + correct/total chip
      · Certificate preview card (mini mock of the PDF that's issued)
      · Download PDF certificate CTA
      · Secondary link to close and return to course view
*/
function CourseCompleteCelebration({ course, result, certificate, doctorName, onDownload, onDismiss }) {
    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onDismiss(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onDismiss]);

    const pct = Math.round((result?.score || 0) * 100);
    const correct = result?.correct ?? 0;
    const total = result?.total ?? 0;
    const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm overflow-y-auto"
            onClick={onDismiss}
            data-testid="course-complete-overlay"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative w-full max-w-xl rounded-3xl bg-white border border-slate-200 shadow-2xl overflow-hidden my-8"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Confetti-like decorative dots (gold, emerald, rose) */}
                <div className="pointer-events-none absolute inset-0 opacity-70">
                    <span className="absolute top-4 left-8 w-2 h-2 rounded-full bg-amber-400" />
                    <span className="absolute top-10 right-12 w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="absolute top-20 left-20 w-1.5 h-1.5 rounded-full bg-rose-400" />
                    <span className="absolute top-6 right-32 w-1.5 h-1.5 rounded-full bg-sky-400" />
                    <span className="absolute bottom-20 left-8 w-2 h-2 rounded-full bg-primary/60" />
                    <span className="absolute bottom-32 right-8 w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span className="absolute bottom-16 left-24 w-1.5 h-1.5 rounded-full bg-emerald-500" />
                </div>

                {/* Top hero — gold Trophy on gradient */}
                <div className="relative bg-gradient-to-br from-amber-50 via-white to-emerald-50 px-8 pt-10 pb-8 text-center">
                    <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white grid place-items-center shadow-lg shadow-amber-500/40 mb-5">
                        <Trophy size={40} weight="fill" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">
                        Course complete
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        You did it.
                    </h2>
                    <p className="text-sm text-slate-600 mt-3 max-w-md mx-auto">
                        You&apos;ve completed <strong>{course?.title}</strong> and earned your Certificate of Completion.
                    </p>

                    {/* Score + result chips */}
                    <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 shadow-sm">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Final score</div>
                            <div className="text-2xl font-bold text-emerald-600 leading-none">{pct}%</div>
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                            <CheckCircle size={12} weight="fill" /> {correct} of {total} correct
                        </div>
                    </div>
                </div>

                {/* Certificate preview card */}
                <div className="relative px-8 py-6 border-t border-slate-100 bg-white">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                        Your certificate
                    </div>
                    <div className="relative rounded-xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50/50 via-white to-white p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 rounded-md bg-primary/10 grid place-items-center">
                                <Trophy size={12} weight="fill" className="text-primary" />
                            </div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
                                Invisique™ · Certificate of Completion
                            </div>
                        </div>
                        <div className="font-display text-lg font-bold text-slate-900 leading-tight">
                            {doctorName || "Doctor"}
                        </div>
                        <div className="text-sm text-slate-700 mt-1 leading-snug">
                            has successfully completed
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mt-0.5 leading-snug">
                            {course?.title}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                            <span>Issued {dateStr}</span>
                            {certificate?.serial && <span>#{certificate.serial}</span>}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="relative px-8 pb-8 pt-2 bg-white space-y-2">
                    <Button
                        size="lg"
                        onClick={onDownload}
                        disabled={!certificate?.id}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="course-complete-download-btn"
                    >
                        <FileText size={14} weight="fill" className="mr-2" />
                        {certificate?.id ? "Download Certificate (PDF)" : "Issuing certificate…"}
                    </Button>
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="w-full text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 py-1"
                        data-testid="course-complete-dismiss"
                    >
                        Back to the course
                    </button>
                </div>
            </div>
        </div>
    );
}


/*  ModuleCompleteCelebration — iter100.157p
    Full-viewport overlay that congratulates the doctor on finishing a
    module. Shows a recap (lessons + quiz score) and one big CTA:
      • Continue → jumps to the first lesson of the next module
      • Take Final Exam → opens FinalExamView (when the finished module is
        the last one AND all prereqs are now met)
      • Back to Academy → falls through to the catalogue (edge case)
    Escape / backdrop click dismisses. */
function ModuleCompleteCelebration({ course, moduleId, lessonProgress, finalConfig, onContinue, onDismiss }) {
    const modules = course?.modules || [];
    const mIdx = modules.findIndex((m) => m.id === moduleId);
    const module = mIdx >= 0 ? modules[mIdx] : null;
    const nextModule = mIdx >= 0 && mIdx < modules.length - 1 ? modules[mIdx + 1] : null;

    // Compute recap
    const recap = useMemo(() => {
        if (!module) return { lessons: 0, quizScore: null };
        const lessons = module.lessons || [];
        const nonQuiz = lessons.filter((l) => l.type !== "quiz").length;
        const quizLesson = lessons.find((l) => l.type === "quiz");
        const quizProg = quizLesson ? lessonProgress[quizLesson.id]?.quiz : null;
        return {
            lessons: nonQuiz,
            quizScore: quizProg ? Math.round((quizProg.score || 0) * 100) : null,
        };
    }, [module, lessonProgress]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onDismiss(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onDismiss]);

    if (!module) return null;

    const isLastModule = !nextModule;
    const nextLessonId = nextModule?.lessons?.[0]?.id;
    const primaryLabel = isLastModule
        ? (finalConfig.enabled ? "Take the Final Exam" : "Finish the course")
        : `Continue to ${nextModule.title.split("·").pop()?.trim() || "the next module"}`;
    const primaryAction = () => {
        if (isLastModule && finalConfig.enabled) return onContinue("final-exam");
        if (nextLessonId) return onContinue({ lessonId: nextLessonId });
        return onDismiss();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
            onClick={onDismiss}
            data-testid={`module-complete-overlay-${moduleId}`}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative w-full max-w-lg rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Confetti-like decorative dots */}
                <div className="pointer-events-none absolute inset-0 opacity-40">
                    <span className="absolute top-4 left-6 w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="absolute top-10 right-10 w-2 h-2 rounded-full bg-amber-400" />
                    <span className="absolute top-16 left-16 w-1.5 h-1.5 rounded-full bg-rose-400" />
                    <span className="absolute top-6 right-24 w-1.5 h-1.5 rounded-full bg-teal-400" />
                    <span className="absolute bottom-16 left-8 w-2 h-2 rounded-full bg-primary/60" />
                    <span className="absolute bottom-24 right-6 w-1.5 h-1.5 rounded-full bg-emerald-500" />
                </div>

                <div className="relative p-8 md:p-10 text-center">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500 text-white grid place-items-center shadow-lg shadow-emerald-500/40 mb-5">
                        <Trophy size={30} weight="fill" />
                    </div>

                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">
                        Module {mIdx + 1} of {modules.length} complete
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-emerald-900">
                        Nice work.
                    </h2>
                    <p className="text-sm text-emerald-800 mt-3 max-w-sm mx-auto">
                        You just finished <strong>{module.title.replace(/^Module \d+ · /, "")}</strong>.
                        {isLastModule
                            ? " Only the final exam stands between you and your certificate."
                            : " Ready for the next module?"}
                    </p>

                    <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-900 text-xs font-semibold">
                            <CheckCircle size={12} weight="fill" className="text-emerald-500" /> {recap.lessons} lesson{recap.lessons === 1 ? "" : "s"} watched
                        </span>
                        {recap.quizScore !== null && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-900 text-xs font-semibold">
                                <Question size={12} weight="fill" className="text-emerald-500" /> Quiz {recap.quizScore}%
                            </span>
                        )}
                    </div>

                    <div className="mt-8 space-y-2">
                        <Button
                            size="lg"
                            onClick={primaryAction}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`module-complete-continue-btn-${moduleId}`}
                        >
                            {primaryLabel}
                            <ArrowRight size={14} weight="bold" className="ml-2" />
                        </Button>
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="text-xs text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                            data-testid={`module-complete-dismiss-${moduleId}`}
                        >
                            I&apos;ll continue later
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


/*  Sidebar nav button — iter100.157nx2
    Just a status-aware nav item. All the exam UI lives in <FinalExamView>
    which renders in the main content area. */
function FinalExamNav({ progress, onOpen, isActive }) {
    const finalExamEntry = progress.enrollment?.final_exam;
    const summary = progress.summary;
    const canTake = summary?.lessons_viewed === summary?.lessons_total
        && summary?.quizzes_passed === summary?.quizzes_total;
    const passed = !!finalExamEntry?.passed;

    const tone = isActive
        ? "bg-primary text-primary-foreground shadow-sm"
        : passed
            ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
            : canTake
                ? "bg-rose-50 text-rose-800 hover:bg-rose-100"
                : "bg-secondary text-muted-foreground cursor-not-allowed";

    const label = passed ? "Passed" : canTake ? "Ready to take" : "Complete all lessons + quizzes first";

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid="course-view-final-exam-nav">
            <button
                type="button"
                onClick={() => canTake && !isActive && onOpen()}
                disabled={!canTake}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${tone}`}
                data-testid="final-exam-nav-open-btn"
            >
                <div className={`w-6 h-6 rounded-md grid place-items-center shrink-0 ${isActive ? "bg-white/20" : "bg-white/70"}`}>
                    <Question size={11} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold">Final Exam</div>
                    <div className="text-[10px]">{label}</div>
                </div>
                {passed && <CheckCircle size={11} weight="fill" className={isActive ? "text-white" : "text-emerald-500"} />}
            </button>
        </div>
    );
}


/*  Full-width exam view — iter100.157nx2
    Lives in the main content column (same slot as a lesson). Renders one of:
      • locked card (prerequisites not met)
      • intro card + Start button
      • active session with all questions + Submit
      • pass/fail result card (with Retake for fails) */
function FinalExamView({ courseId, progress, onExamPassed, openTrigger = 0, onCoursePassed }) {
    const config = progress.final_exam_config || {};
    const finalExamEntry = progress.enrollment?.final_exam;
    const summary = progress.summary;
    const canTake = summary?.lessons_viewed === summary?.lessons_total
        && summary?.quizzes_passed === summary?.quizzes_total;
    const passed = !!finalExamEntry?.passed;

    const [session, setSession] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    // iter100.157q — when the user arrives via the sticky "Take Final Exam"
    // CTA we auto-start the session. Between render and the API returning
    // we don't want to flash the "intro card". Instead render a lightweight
    // loading state.
    const [autoStartInFlight, setAutoStartInFlight] = useState(false);

    const start = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/academy/courses/${courseId}/final-exam/start`);
            setSession(data);
            setAnswers({});
            setResult(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start final exam.");
        } finally {
            setLoading(false);
            setAutoStartInFlight(false);
        }
    }, [courseId]);

    // iter100.157nx — auto-start when the view opens from the sticky footer CTA
    // (openTrigger counter increments on every "Take Final Exam" click).
    useEffect(() => {
        if (!openTrigger) return;
        if (!canTake || passed) return;
        if (!session && !loading) {
            setAutoStartInFlight(true);
            start();
        }
    }, [openTrigger, canTake, passed, session, loading, start]);

    const submit = async () => {
        if (!session) return;
        if (Object.keys(answers).length !== session.total_questions) {
            toast.error("Answer every question first.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                session_id: session.session_id,
                answers: session.questions.map((q) => answers[q.id] ?? -1),
            };
            const { data } = await api.post(`/academy/courses/${courseId}/final-exam/submit`, payload);
            setResult(data);
            if (data.passed) {
                // iter100.157q — Fire the parent's full-viewport course-complete
                // celebration. Score + certificate download live there.
                onCoursePassed?.(data);
            } else {
                toast.error(`Score ${Math.round(data.score * 100)}% · try again.`);
            }
            await onExamPassed?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit exam.");
        } finally {
            setLoading(false);
        }
    };

    const answered = Object.keys(answers).length;
    const totalQ = session?.total_questions || 0;

    // ── Locked: prerequisites not met ────────────────────────────────
    if (!canTake && !passed) {
        const remainingLessons = (summary?.lessons_total || 0) - (summary?.lessons_viewed || 0);
        const remainingQuizzes = (summary?.quizzes_total || 0) - (summary?.quizzes_passed || 0);
        return (
            <div className="max-w-3xl mx-auto" data-testid="final-exam-view-locked">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-white/70 grid place-items-center mb-4">
                        <Question size={24} weight="fill" className="text-amber-700" />
                    </div>
                    <h1 className="font-display text-2xl font-bold text-amber-900">Final Exam is locked</h1>
                    <p className="text-sm text-amber-800 mt-2 max-w-lg mx-auto">
                        Complete every lesson and pass every module quiz before you can attempt the final exam.
                    </p>
                    <div className="mt-6 grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
                        <div className="rounded-xl bg-white/70 border border-amber-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Lessons</div>
                            <div className="text-xl font-bold text-amber-900 mt-0.5">
                                {summary?.lessons_viewed || 0} / {summary?.lessons_total || 0}
                            </div>
                            {remainingLessons > 0 && (
                                <div className="text-[10px] text-amber-700 mt-1">{remainingLessons} left</div>
                            )}
                        </div>
                        <div className="rounded-xl bg-white/70 border border-amber-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Quizzes passed</div>
                            <div className="text-xl font-bold text-amber-900 mt-0.5">
                                {summary?.quizzes_passed || 0} / {summary?.quizzes_total || 0}
                            </div>
                            {remainingQuizzes > 0 && (
                                <div className="text-[10px] text-amber-700 mt-1">{remainingQuizzes} left</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Passed: certificate view (no re-take unless we allow it in the future) ─
    if (passed && !session) {
        return (
            <div className="max-w-3xl mx-auto" data-testid="final-exam-view-passed">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-white/70 grid place-items-center mb-4">
                        <Trophy size={24} weight="fill" className="text-emerald-700" />
                    </div>
                    <h1 className="font-display text-2xl font-bold text-emerald-900">Final Exam Passed</h1>
                    <p className="text-sm text-emerald-800 mt-2">
                        Score: <span className="font-bold">{Math.round((finalExamEntry?.score || 0) * 100)}%</span>
                        {finalExamEntry?.attempted_at && (
                            <> · {new Date(finalExamEntry.attempted_at).toLocaleDateString()}</>
                        )}
                    </p>
                    <p className="text-sm text-emerald-800 mt-1">
                        Your certificate is ready — check the header bar or the &ldquo;My Certifications&rdquo; tab.
                    </p>
                </div>
            </div>
        );
    }

    // ── Result card ──────────────────────────────────────────────────
    if (result) {
        return (
            <div className="max-w-3xl mx-auto" data-testid="final-exam-result">
                <div
                    className={`rounded-2xl border p-8 text-center ${
                        result.passed
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-rose-50 border-rose-200"
                    }`}
                >
                    <div className={`w-14 h-14 mx-auto rounded-full grid place-items-center mb-4 bg-white/70`}>
                        {result.passed
                            ? <Trophy size={24} weight="fill" className="text-emerald-700" />
                            : <ClockCounterClockwise size={24} weight="fill" className="text-rose-700" />}
                    </div>
                    <h1 className={`font-display text-2xl font-bold ${result.passed ? "text-emerald-900" : "text-rose-900"}`}>
                        {result.passed ? "You passed!" : "Not quite — try again"}
                    </h1>
                    <div className={`text-lg font-bold mt-2 ${result.passed ? "text-emerald-800" : "text-rose-800"}`}>
                        {result.correct}/{result.total} correct · {Math.round(result.score * 100)}%
                    </div>
                    <p className={`text-sm mt-2 ${result.passed ? "text-emerald-800" : "text-rose-800"}`}>
                        {result.passed
                            ? "Your certificate has been issued — see the header bar or the \u201cMy Certifications\u201d tab."
                            : `Pass score is ${Math.round((config.pass_score || 0.7) * 100)}%.`}
                    </p>
                    {!result.passed && (
                        <div className="mt-6">
                            <Button
                                onClick={() => { setSession(null); setResult(null); setAnswers({}); start(); }}
                                data-testid="final-exam-retake-btn"
                            >
                                Retake the exam
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Intro card + Start button ────────────────────────────────────
    if (!session) {
        // Auto-start in flight? Show a lightweight loader instead of flashing
        // the intro card for ~200ms while the /start API returns.
        if (autoStartInFlight) {
            return (
                <div className="max-w-3xl mx-auto" data-testid="final-exam-view-loading">
                    <div className="rounded-2xl border border-rose-100 bg-white p-10 text-center">
                        <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 grid place-items-center mb-4 animate-pulse">
                            <Question size={24} weight="fill" className="text-rose-500/70" />
                        </div>
                        <h1 className="font-display text-2xl font-bold text-slate-900">Preparing your Final Exam…</h1>
                        <p className="text-sm text-slate-500 mt-2">Loading {config.question_count} questions.</p>
                    </div>
                </div>
            );
        }
        return (
            <div className="max-w-3xl mx-auto" data-testid="final-exam-view-intro">
                <div className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-white/70 grid place-items-center mb-4">
                        <Question size={24} weight="fill" className="text-rose-700" />
                    </div>
                    <h1 className="font-display text-3xl font-bold text-rose-900">Final Exam</h1>
                    <p className="text-sm text-rose-800 mt-3 max-w-lg mx-auto">
                        You&apos;ve completed all modules — one graded assessment stands between you and your certificate.
                    </p>
                    <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto text-left">
                        <div className="rounded-xl bg-white border border-rose-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Questions</div>
                            <div className="text-xl font-bold text-rose-900 mt-0.5">{config.question_count}</div>
                        </div>
                        <div className="rounded-xl bg-white border border-rose-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Pass score</div>
                            <div className="text-xl font-bold text-rose-900 mt-0.5">{Math.round((config.pass_score || 0.7) * 100)}%</div>
                        </div>
                    </div>
                    <div className="mt-6">
                        <Button
                            size="lg"
                            onClick={start}
                            disabled={loading}
                            data-testid="final-exam-start-btn"
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                        >
                            {loading ? "Loading…" : "Start Final Exam"}
                        </Button>
                    </div>
                    <p className="text-[11px] text-rose-700/80 mt-3">
                        Answer every question. You can retake if you don&apos;t pass on the first try.
                    </p>
                </div>
            </div>
        );
    }

    // ── Active session ───────────────────────────────────────────────
    return (
        <div className="max-w-4xl mx-auto" data-testid="final-exam-session">
            <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[10px] font-bold uppercase tracking-wider">
                    <Question size={11} weight="fill" /> Final Exam · {config.question_count} questions
                </div>
                <div className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/5 border border-primary/20 text-primary text-[10px] font-bold">
                    {answered} / {totalQ} answered
                </div>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
                Final Exam
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
                Pass with {Math.round((config.pass_score || 0.7) * 100)}% or higher to unlock your certificate.
            </p>

            <div className="mt-8 space-y-4">
                {session.questions.map((q, i) => (
                    <div
                        key={q.id}
                        className="rounded-2xl border border-border bg-card p-5"
                        data-testid={`final-exam-q-${i}`}
                    >
                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                            Question {i + 1} of {totalQ}
                        </div>
                        <RichTextView html={q.prompt} className="text-base font-semibold mt-1.5 mb-4" />
                        <div className="space-y-2">
                            {q.options.map((opt, oIdx) => (
                                <label
                                    key={oIdx}
                                    className={`flex items-center gap-2.5 p-3 rounded-lg cursor-pointer text-sm transition-colors ${
                                        answers[q.id] === oIdx
                                            ? "bg-primary/5 border border-primary/40 ring-1 ring-primary/20"
                                            : "border border-border hover:bg-secondary/40"
                                    }`}
                                    data-testid={`final-exam-q-${i}-opt-${oIdx}`}
                                >
                                    <input
                                        type="radio"
                                        name={`fe-${q.id}`}
                                        checked={answers[q.id] === oIdx}
                                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: oIdx }))}
                                        className="h-4 w-4 shrink-0"
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
                <Button
                    size="lg"
                    onClick={submit}
                    disabled={loading || answered !== totalQ}
                    data-testid="final-exam-submit-btn"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                    {loading ? "Grading…" : `Submit Final Exam (${answered}/${totalQ})`}
                </Button>
                {answered !== totalQ && (
                    <span className="text-xs text-muted-foreground">
                        Answer every question to enable submission.
                    </span>
                )}
            </div>
        </div>
    );
}


function LessonPlayer({ lesson, courseId, lessonProgress, onProgressUpdate }) {
    const meta = LESSON_TYPE_META[lesson.type] || LESSON_TYPE_META.text;
    const prog = lessonProgress?.[lesson.id] || {};
    const alreadyViewed = !!prog.viewed_at;

    // iter100.157n — Track audio-progress for narration-gated lessons so we
    // only fire the mark-viewed request AFTER ≥90% of the audio has played.
    const hasNarration = !!lesson.narration_media_path;
    const gateStrict = hasNarration && !!lesson.narration_gate_strict;
    const [audioProgress, setAudioProgress] = useState(0);

    // For video / text / file lessons: mark viewed once when the user opens
    // the lesson. Idempotent server-side. Quiz lessons mark-viewed on submit.
    // Narration-gated lessons wait until audio ≥ 0.9.
    //
    // iter100.157s — Track marked lessons per-lesson-id. Previously this was
    // a single boolean ref that persisted across lesson changes, so only the
    // FIRST lesson opened in a session ever got auto-marked — leaving the
    // rest permanently "unviewed" and blocking module completion + the
    // celebration overlay from ever firing.
    const markedRef = useRef(new Set());
    useEffect(() => {
        if (lesson.type === "quiz") return;      // quiz handles its own view-mark
        if (alreadyViewed) return;
        if (markedRef.current.has(lesson.id)) return;
        if (gateStrict && audioProgress < 0.9) return;
        markedRef.current.add(lesson.id);
        (async () => {
            try {
                await api.post(`/academy/lessons/${lesson.id}/complete`, null, {
                    params: gateStrict ? { audio_progress: audioProgress } : undefined,
                });
                await onProgressUpdate?.();
            } catch { /* silent */ }
        })();
    }, [lesson.id, lesson.type, alreadyViewed, onProgressUpdate, gateStrict, audioProgress]);

    return (
        <div className="max-w-4xl mx-auto" data-testid={`lesson-player-${lesson.id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-[10px] font-bold uppercase tracking-wider">
                    <meta.Icon size={11} weight="fill" /> {meta.label} · Module: {lesson.module?.title}
                </div>
                {alreadyViewed && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-bold" data-testid={`lesson-viewed-chip-${lesson.id}`}>
                        <CheckCircle size={10} weight="fill" /> Viewed
                    </span>
                )}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="lesson-player-title">{lesson.title}</h1>

            <div className="mt-6 space-y-6">
                {/* Narration player — shown above the body for both `narrated` slide lessons AND text lessons that opted-in */}
                {hasNarration && (lesson.type === "narrated" || lesson.type === "text") && (
                    <NarrationPlayer
                        lesson={lesson}
                        gateStrict={gateStrict}
                        onProgress={setAudioProgress}
                    />
                )}

                {lesson.type === "video" && <VideoPlayer url={lesson.video_url} />}
                {(lesson.type === "text" || lesson.type === "narrated") && <TextPlayer body={lesson.text_body} />}
                {lesson.type === "file" && <FilePlayer mediaId={lesson.file_media_id} />}
                {lesson.type === "quiz" && (
                    <QuizPlayer
                        lesson={lesson}
                        courseId={courseId}
                        priorQuiz={prog.quiz}
                        onGraded={onProgressUpdate}
                    />
                )}
            </div>
        </div>
    );
}


function VideoPlayer({ url }) {
    const embed = toEmbedUrl(url);
    if (!embed) {
        return <div className="p-6 text-center border border-dashed border-border rounded-xl text-muted-foreground text-sm">No video URL set for this lesson.</div>;
    }
    return (
        <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-border" data-testid="video-embed">
            <iframe
                title="Course video"
                src={embed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
            />
        </div>
    );
}


function TextPlayer({ body }) {
    if (!body) {
        return <div className="text-sm text-muted-foreground italic">This text lesson is empty.</div>;
    }
    return (
        <article
            className="prose prose-slate max-w-none text-foreground [&_h1]:font-display [&_h2]:font-display [&_h3]:font-display"
            data-testid="text-body"
            dangerouslySetInnerHTML={{ __html: expandEmbeds(body) }}
        />
    );
}


function FilePlayer({ mediaId }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);

    useEffect(() => {
        setBlobUrl(null);
        if (!mediaId) return undefined;
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        (async () => {
            try {
                const token = localStorage.getItem("ca_token");
                const resp = await fetch(`${API}/course-builder/media/${mediaId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (!resp.ok) throw new Error("fetch failed");
                const blob = await resp.blob();
                setBlobUrl(URL.createObjectURL(blob));
            } catch (e) {
                if (e.name !== "AbortError") toast.error("Couldn't load file.");
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            controller.abort();
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [mediaId]);

    if (!mediaId) return <div className="text-sm text-muted-foreground italic">No file attached.</div>;
    return (
        <div className="p-6 border border-border rounded-2xl bg-card text-center" data-testid="file-player">
            <FileArrowUp size={36} weight="duotone" className="text-primary mx-auto mb-3" />
            <div className="text-sm font-semibold">Course resource</div>
            <p className="text-xs text-muted-foreground mt-1">Download the attached file to view offline.</p>
            {loading ? (
                <div className="mt-4 text-xs text-muted-foreground">Preparing download…</div>
            ) : blobUrl ? (
                <Button asChild className="mt-4">
                    <a href={blobUrl} download data-testid="file-download-btn">
                        <DownloadSimple size={13} weight="fill" className="mr-1.5" /> Download file
                    </a>
                </Button>
            ) : null}
        </div>
    );
}


function QuizPlayer({ lesson, priorQuiz, onGraded }) {
    const quiz = lesson.quiz;
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    if (!quiz || !(quiz.questions || []).length) {
        return <div className="text-sm text-muted-foreground italic">This quiz has no questions.</div>;
    }

    const setAnswer = (qId, optIdx) => {
        if (result) return;
        setAnswers((prev) => ({ ...prev, [qId]: optIdx }));
    };

    const onSubmit = async () => {
        const unanswered = quiz.questions.find((q) => answers[q.id] === undefined);
        if (unanswered) {
            toast.error("Please answer every question before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { answers: quiz.questions.map((q) => answers[q.id] ?? -1) };
            const { data } = await api.post(`/academy/quizzes/${lesson.id}/submit`, payload);
            setResult(data);
            if (data.passed) {
                toast.success(`Passed · ${Math.round(data.score * 100)}%`);
                if (data.certificate) toast.success("Certificate issued!");
            } else {
                toast.error(`Score ${Math.round(data.score * 100)}% · retake to improve.`);
            }
            await onGraded?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit quiz.");
        } finally {
            setSubmitting(false);
        }
    };

    const onRetake = () => {
        setAnswers({});
        setResult(null);
    };

    return (
        <div className="space-y-4" data-testid="quiz-player">
            <div className="bg-secondary/40 border border-border rounded-xl p-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                    <span className="font-semibold">{quiz.questions.length} questions</span>
                    <span className="text-muted-foreground">Pass: {Math.round((quiz.pass_score || 0) * 100)}%</span>
                    <span className="text-muted-foreground">Max attempts: {quiz.max_attempts}</span>
                    {priorQuiz?.best_score !== undefined && (
                        <span className="text-muted-foreground">Best: {Math.round((priorQuiz.best_score || 0) * 100)}%</span>
                    )}
                </div>
                {result && quiz.retake_allowed && (
                    <Button size="sm" variant="outline" onClick={onRetake} data-testid="quiz-retake-btn">Retake</Button>
                )}
            </div>

            {quiz.questions.map((q, i) => {
                const fb = result?.feedback?.[i];
                return (
                    <div key={q.id} className="bg-card border border-border rounded-xl p-4" data-testid={`quiz-player-q-${i}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Q{i + 1}</div>
                        <RichTextView html={q.prompt} className="text-sm font-semibold mb-3" />
                        <div className="space-y-1.5">
                            {q.options.map((opt, oIdx) => {
                                const picked = answers[q.id] === oIdx;
                                const correct = fb && oIdx === fb.correct_index;
                                const wrongPick = fb && !fb.is_correct && oIdx === fb.chosen;
                                let cls = "border-border hover:bg-secondary/40";
                                if (result) {
                                    if (correct) cls = "bg-emerald-50 border-emerald-400 text-emerald-900";
                                    else if (wrongPick) cls = "bg-rose-50 border-rose-400 text-rose-900";
                                    else cls = "border-border opacity-70";
                                } else if (picked) {
                                    cls = "bg-primary/10 border-primary/40";
                                }
                                return (
                                    <label
                                        key={oIdx}
                                        className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${cls} ${result ? "cursor-default" : "cursor-pointer"}`}
                                        data-testid={`quiz-option-${i}-${oIdx}`}
                                    >
                                        <input
                                            type="radio"
                                            name={`q-${q.id}`}
                                            checked={picked}
                                            onChange={() => setAnswer(q.id, oIdx)}
                                            disabled={!!result}
                                            className="h-4 w-4"
                                        />
                                        <span className="text-sm flex-1">{opt}</span>
                                        {correct && <CheckCircle size={13} weight="fill" className="text-emerald-500" />}
                                    </label>
                                );
                            })}
                        </div>
                        {fb && fb.rationale && (
                            <div className={`mt-2 text-[11px] p-2 rounded border ${fb.is_correct ? "bg-emerald-50/60 border-emerald-100 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
                                <strong>Rationale:</strong> <RichTextView html={fb.rationale} className="inline-block" />
                            </div>
                        )}
                    </div>
                );
            })}

            {!result ? (
                <Button onClick={onSubmit} disabled={submitting} data-testid="quiz-submit-btn">
                    <CheckCircle size={13} weight="fill" className="mr-1.5" /> {submitting ? "Submitting…" : "Submit quiz"}
                </Button>
            ) : (
                <div className={`border rounded-xl p-4 text-sm ${result.passed ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="quiz-result">
                    <div className="font-bold flex items-center gap-1.5">
                        {result.passed ? <CheckCircle size={13} weight="fill" /> : <ClockCounterClockwise size={13} weight="fill" />}
                        {result.passed ? "Passed" : "Not passed yet"} · {Math.round(result.score * 100)}%
                    </div>
                    <p className="text-xs mt-1">
                        {result.correct}/{result.total} correct.
                        {result.certificate && (
                            <> Your certificate has been issued — see the header bar or the &quot;My Certifications&quot; tab.</>
                        )}
                    </p>
                </div>
            )}
        </div>
    );
}


/* ═════════════════════════════════════════════════════════════════════
   Sticky bottom navigation footer — iter100.113t
   ═════════════════════════════════════════════════════════════════════ */

function CourseFooter({
    course, courseId, lesson, lessonProgress, summary, cert, isTrulyCompleted,
    prevLesson, nextLesson, activeIndex, total, finalConfig,
    onSelect, onProgressUpdate, onRequestFinalExam,
}) {
    const prog = lessonProgress[lesson.id] || {};
    const isViewed = !!prog.viewed_at;
    const quizPassed = lesson.type === "quiz" && prog.quiz?.passed;
    const isDone = isViewed && (lesson.type !== "quiz" || quizPassed);

    // Auto-advance countdown state
    const [countdown, setCountdown] = useState(null);
    const timerRef = useRef(null);
    useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

    const cancelAutoAdvance = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        setCountdown(null);
    }, []);

    const startAutoAdvance = useCallback(() => {
        if (!nextLesson) return;
        cancelAutoAdvance();
        let n = 5;
        setCountdown(n);
        timerRef.current = setInterval(() => {
            n -= 1;
            if (n <= 0) {
                clearInterval(timerRef.current);
                setCountdown(null);
                onSelect(nextLesson.id);
            } else {
                setCountdown(n);
            }
        }, 1000);
    }, [nextLesson, onSelect, cancelAutoAdvance]);

    // Drawers state
    const [notesOpen, setNotesOpen] = useState(false);
    const [askOpen, setAskOpen] = useState(false);

    // Position — locate the current module
    const moduleInfo = useMemo(() => {
        let mIdx = -1, mTitle = "", localIdx = -1, localTotal = 0;
        (course.modules || []).forEach((m, i) => {
            const idx = (m.lessons || []).findIndex((l) => l.id === lesson.id);
            if (idx >= 0) { mIdx = i; mTitle = m.title; localIdx = idx; localTotal = (m.lessons || []).length; }
        });
        return { mIdx, mTitle, localIdx, localTotal, totalMods: (course.modules || []).length };
    }, [course, lesson.id]);

    // Smart next CTA label + intent
    const smartCta = useMemo(() => {
        const isLast = activeIndex === total - 1;
        // iter100.157r — Only show "View Certificate" when the current run
        // is actually complete. After a Retake, `cert` still exists but the
        // doctor is on a fresh run — treat it like a normal in-progress course.
        if (cert && isTrulyCompleted) return { label: "View Certificate", intent: "cert", disabled: false };
        // iter100.157q — For ANY quiz lesson (not just the last one), a doctor
        // must pass the quiz before the Next button unlocks. Un-passed quizzes
        // in the middle of the course used to let doctors skip forward.
        if (lesson.type === "quiz" && !isDone) {
            return { label: "Submit quiz to continue", intent: "wait", disabled: true };
        }
        if (!isLast) return { label: "Next lesson", intent: "next", disabled: !nextLesson };
        // Last lesson
        if (!isDone) {
            return { label: "Mark complete to continue", intent: "wait", disabled: true };
        }
        if (finalConfig.enabled && !prog.final_passed) {
            return { label: "Take Final Exam", intent: "final", disabled: false };
        }
        if (summary?.all_met) return { label: "Get My Certificate", intent: "cert-ready", disabled: false };
        return { label: "Continue", intent: "next", disabled: !nextLesson };
    }, [activeIndex, total, cert, isTrulyCompleted, nextLesson, isDone, lesson.type, finalConfig.enabled, prog.final_passed, summary]);

    const onNextClick = useCallback(() => {
        if (smartCta.intent === "cert" && cert) {
            downloadCertificateFile(cert.id);
            return;
        }
        if (smartCta.intent === "next" && nextLesson) {
            onSelect(nextLesson.id);
            cancelAutoAdvance();
            return;
        }
        if (smartCta.intent === "final") {
            // iter100.157nx — Open the sidebar Final Exam panel + auto-start
            // the session directly. Also scroll it into view so the user
            // sees the exam immediately.
            onRequestFinalExam?.();
            const el = document.querySelector('[data-testid="course-view-final-exam-nav"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (smartCta.intent === "cert-ready") {
            toast.success("Course complete! Your certificate is being issued.");
            onProgressUpdate?.();
        }
    }, [smartCta.intent, cert, nextLesson, onSelect, onProgressUpdate, cancelAutoAdvance, onRequestFinalExam]);

    const onMarkComplete = useCallback(async (e) => {
        // Only meaningful for non-quiz lessons (quizzes auto-complete on pass).
        if (lesson.type === "quiz") return;
        if (isViewed) return;
        try {
            await api.post(`/academy/lessons/${lesson.id}/complete`);
            toast.success("Marked complete");
            await onProgressUpdate?.();
            if (nextLesson) startAutoAdvance();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't mark complete.");
        }
    }, [lesson.id, lesson.type, isViewed, nextLesson, onProgressUpdate, startAutoAdvance]);

    const onToggleBookmark = useCallback(async () => {
        try {
            const { data } = await api.post(`/academy/lessons/${lesson.id}/bookmark`);
            toast.success(data.bookmarked ? "Bookmarked" : "Bookmark removed");
            await onProgressUpdate?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't toggle bookmark.");
        }
    }, [lesson.id, onProgressUpdate]);

    const onRate = useCallback(async (rating) => {
        try {
            await api.post(`/academy/lessons/${lesson.id}/rate`, { rating });
            toast.success(rating === "up" ? "Thanks for the thumbs up!" : "Thanks — feedback noted.");
            await onProgressUpdate?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save rating.");
        }
    }, [lesson.id, onProgressUpdate]);

    const onShare = useCallback(async () => {
        const url = `${window.location.origin}/doctor/academy/courses/${courseId}?lesson=${lesson.id}`;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                toast.success("Lesson link copied — share with a colleague.");
            } else {
                window.open(url, "_blank");
            }
        } catch { window.open(url, "_blank"); }
    }, [courseId, lesson.id]);

    return (
        <>
            {/* Auto-advance banner (floats above footer) */}
            {countdown !== null && nextLesson && (
                <div
                    className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-primary text-primary-foreground px-4 py-2.5 rounded-full shadow-lg flex items-center gap-3 text-xs font-semibold"
                    data-testid="course-footer-auto-advance"
                >
                    <ArrowRight size={13} weight="bold" />
                    <span>Advancing to <strong>{nextLesson.title}</strong> in {countdown}s…</span>
                    <button
                        type="button"
                        onClick={cancelAutoAdvance}
                        className="ml-1 h-6 px-2 rounded bg-primary-foreground/15 hover:bg-primary-foreground/25 text-[10px] font-bold uppercase tracking-wider transition-colors"
                        data-testid="course-footer-auto-advance-cancel"
                    >
                        Cancel
                    </button>
                </div>
            )}

            <footer
                className="fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border shadow-[0_-4px_20px_rgba(0,45,57,0.06)]"
                data-testid="course-footer"
            >
                {/* Progress rail — thin, full-width */}
                <div className="h-1.5 bg-secondary/50 relative" data-testid="course-footer-progress-rail">
                    <div
                        className="h-full bg-gradient-to-r from-primary to-rose-500 transition-all duration-500"
                        style={{ width: `${summary?.percent || 0}%` }}
                    />
                </div>

                <div className="px-4 md:px-6 py-2.5 flex items-center gap-2 flex-wrap md:flex-nowrap">
                    {/* Position + module info */}
                    <div className="flex items-center gap-2 min-w-0 flex-1 md:flex-initial">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary shrink-0">
                            {activeIndex + 1} / {total}
                        </div>
                        <div className="h-4 w-px bg-border shrink-0" />
                        <div className="text-[11px] text-muted-foreground truncate min-w-0" data-testid="course-footer-module-position">
                            <span className="font-semibold text-foreground">Lesson {moduleInfo.localIdx + 1}</span> of {moduleInfo.localTotal}
                            <span className="hidden lg:inline"> · Module {moduleInfo.mIdx + 1} of {moduleInfo.totalMods}</span>
                        </div>
                        {summary && (
                            <div className="hidden md:flex items-center gap-1 text-[10.5px] text-muted-foreground pl-2 border-l border-border">
                                <strong className="text-foreground tabular-nums">{summary.percent}%</strong>
                                <span className="text-muted-foreground/80">· {summary.lessons_viewed}/{summary.lessons_total} lessons</span>
                            </div>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block h-6 w-px bg-border" />

                    {/* Mark complete + secondary action icons */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {lesson.type !== "quiz" && (
                            <label
                                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border cursor-pointer transition-colors ${
                                    isViewed ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white hover:bg-secondary/40 border-border text-foreground"
                                }`}
                                title={isViewed ? "Marked complete" : "Mark this lesson complete"}
                                data-testid="course-footer-mark-complete-toggle"
                            >
                                <input
                                    type="checkbox"
                                    checked={isViewed}
                                    onChange={onMarkComplete}
                                    disabled={isViewed}
                                    className="h-3.5 w-3.5"
                                    data-testid="course-footer-mark-complete-checkbox"
                                />
                                <span className="text-[11px] font-semibold">{isViewed ? "Complete" : "Mark complete"}</span>
                            </label>
                        )}

                        <FooterIconButton
                            icon={BookmarkSimple}
                            active={!!prog.bookmarked}
                            onClick={onToggleBookmark}
                            label={prog.bookmarked ? "Bookmarked" : "Bookmark"}
                            activeClass="bg-rose-50 border-rose-300 text-rose-700"
                            weight={prog.bookmarked ? "fill" : "duotone"}
                            testid="course-footer-bookmark"
                        />
                        <FooterIconButton
                            icon={NotePencil}
                            active={!!(prog.notes && prog.notes.trim()) || notesOpen}
                            onClick={() => setNotesOpen(true)}
                            label="Notes"
                            activeClass="bg-amber-50 border-amber-300 text-amber-700"
                            testid="course-footer-notes"
                        />
                        <FooterIconButton
                            icon={ThumbsUp}
                            active={prog.rating === "up"}
                            onClick={() => onRate("up")}
                            label="Thumbs up"
                            activeClass="bg-emerald-50 border-emerald-300 text-emerald-700"
                            weight={prog.rating === "up" ? "fill" : "duotone"}
                            testid="course-footer-rate-up"
                        />
                        <FooterIconButton
                            icon={ThumbsDown}
                            active={prog.rating === "down"}
                            onClick={() => onRate("down")}
                            label="Thumbs down"
                            activeClass="bg-slate-100 border-slate-300 text-slate-700"
                            weight={prog.rating === "down" ? "fill" : "duotone"}
                            testid="course-footer-rate-down"
                        />
                        <FooterIconButton
                            icon={ChatCircleDots}
                            onClick={() => setAskOpen(true)}
                            label="Ask CoE"
                            testid="course-footer-ask"
                        />
                        <FooterIconButton
                            icon={ShareNetwork}
                            onClick={onShare}
                            label="Share"
                            testid="course-footer-share"
                        />
                        {lesson.type === "text" && lesson.text_body && (
                            <FooterIconButton
                                icon={Printer}
                                onClick={() => printTranscript(lesson)}
                                label="Print"
                                testid="course-footer-print"
                            />
                        )}
                    </div>

                    {/* Prev / Next CTA cluster (right-aligned) */}
                    <div className="ml-auto flex items-center gap-1.5">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => prevLesson && onSelect(prevLesson.id)}
                            disabled={!prevLesson}
                            className="h-8 px-2.5 text-[11px] font-semibold"
                            title="Previous lesson (← arrow)"
                            data-testid="course-footer-prev-btn"
                        >
                            <ArrowLeft size={12} weight="bold" className="mr-1" /> Prev
                        </Button>
                        <Button
                            size="sm"
                            onClick={onNextClick}
                            disabled={smartCta.disabled}
                            className={`h-8 px-3 text-[11px] font-bold ${
                                smartCta.intent === "cert-ready" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                                smartCta.intent === "cert" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                                smartCta.intent === "final" ? "bg-rose-600 hover:bg-rose-700 text-white" :
                                "bg-primary hover:bg-primary/90 text-primary-foreground"
                            }`}
                            title={smartCta.intent === "next" ? "Next lesson (→ arrow)" : smartCta.label}
                            data-testid="course-footer-next-btn"
                        >
                            {smartCta.label}
                            {smartCta.intent === "next" && <ArrowRight size={12} weight="bold" className="ml-1" />}
                            {smartCta.intent === "cert-ready" && <Trophy size={12} weight="fill" className="ml-1" />}
                            {smartCta.intent === "cert" && <Trophy size={12} weight="fill" className="ml-1" />}
                        </Button>
                    </div>
                </div>
            </footer>

            {/* Notes drawer */}
            {notesOpen && (
                <NotesDrawer
                    lesson={lesson}
                    initialBody={prog.notes || ""}
                    onClose={() => setNotesOpen(false)}
                    onSaved={onProgressUpdate}
                />
            )}
            {/* Ask CoE drawer */}
            {askOpen && (
                <AskDrawer
                    lesson={lesson}
                    onClose={() => setAskOpen(false)}
                />
            )}
        </>
    );
}


function FooterIconButton({ icon: Icon, active, onClick, label, activeClass, weight, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-1 h-8 px-2 rounded-md border transition-colors text-[11px] font-semibold whitespace-nowrap ${
                active ? (activeClass || "bg-primary/10 border-primary/30 text-primary") : "bg-white border-border text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
            }`}
            title={label}
            aria-label={label}
            data-testid={testid}
        >
            <Icon size={13} weight={weight || (active ? "fill" : "duotone")} />
            <span className="hidden lg:inline">{label}</span>
        </button>
    );
}


/* ═════════════════════════════════════════════════════════════════════
   Notes drawer — private study notes per lesson, auto-saved.
   ═════════════════════════════════════════════════════════════════════ */

function NotesDrawer({ lesson, initialBody, onClose, onSaved }) {
    const [body, setBody] = useState(initialBody || "");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const saveTimerRef = useRef(null);

    useEffect(() => {
        if (!dirty) return undefined;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            setSaving(true);
            try {
                await api.put(`/academy/lessons/${lesson.id}/notes`, { body });
                setDirty(false);
                await onSaved?.();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't save notes.");
            } finally {
                setSaving(false);
            }
        }, 800);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [body, dirty, lesson.id, onSaved]);

    // Esc to close
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    return (
        <>
            <div className="fixed inset-0 bg-slate-900/30 z-50" onClick={onClose} data-testid="notes-drawer-backdrop" />
            <aside
                className="fixed top-0 right-0 h-full w-[min(420px,92vw)] bg-card border-l border-border z-50 shadow-xl flex flex-col"
                data-testid="notes-drawer"
            >
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-600">My Notes</div>
                        <div className="text-xs font-semibold text-foreground truncate">{lesson.title}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground" data-testid="notes-drawer-save-state">
                            {saving ? "Saving…" : dirty ? "Unsaved changes" : "Saved"}
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-7 h-7 rounded-md hover:bg-secondary/60 grid place-items-center text-muted-foreground"
                            aria-label="Close notes"
                            data-testid="notes-drawer-close-btn"
                        >
                            <X size={13} weight="bold" />
                        </button>
                    </div>
                </div>
                <div className="p-4 flex-1 min-h-0 flex flex-col">
                    <textarea
                        value={body}
                        onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                        placeholder="Jot down key clinical takeaways, questions to look up, or things to try on your next case…"
                        className="flex-1 w-full text-sm bg-secondary/30 border border-border rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                        maxLength={10000}
                        data-testid="notes-drawer-textarea"
                    />
                    <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{body.length}/10 000 characters · auto-saves as you type</span>
                        <span>Private — only you can see this</span>
                    </div>
                </div>
            </aside>
        </>
    );
}


/* ═════════════════════════════════════════════════════════════════════
   Ask CoE drawer — send a question to the support queue.
   ═════════════════════════════════════════════════════════════════════ */

function AskDrawer({ lesson, onClose }) {
    const [question, setQuestion] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    const submit = async () => {
        const trimmed = question.trim();
        if (trimmed.length < 3) { toast.error("Ask a question of at least 3 characters."); return; }
        setSubmitting(true);
        try {
            await api.post(`/academy/lessons/${lesson.id}/ask`, { question: trimmed });
            setSent(true);
            toast.success("Question sent — the CoE team will reply within 1 business day.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send question.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-slate-900/30 z-50" onClick={onClose} data-testid="ask-drawer-backdrop" />
            <aside
                className="fixed top-0 right-0 h-full w-[min(420px,92vw)] bg-card border-l border-border z-50 shadow-xl flex flex-col"
                data-testid="ask-drawer"
            >
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">Ask CoE Support</div>
                        <div className="text-xs font-semibold text-foreground truncate">About: {lesson.title}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-7 h-7 rounded-md hover:bg-secondary/60 grid place-items-center text-muted-foreground"
                        aria-label="Close"
                        data-testid="ask-drawer-close-btn"
                    >
                        <X size={13} weight="bold" />
                    </button>
                </div>
                <div className="p-4 flex-1 min-h-0 flex flex-col">
                    {sent ? (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 text-center" data-testid="ask-drawer-sent">
                            <CheckCircle size={28} weight="fill" className="text-emerald-600 mx-auto mb-2" />
                            <div className="font-display font-bold text-sm">Question sent</div>
                            <p className="text-xs mt-1 leading-relaxed">
                                Our CoE Support team will follow up within 1 business day.
                                In the meantime, feel free to continue with the course.
                            </p>
                            <Button
                                onClick={onClose}
                                size="sm"
                                variant="outline"
                                className="mt-3 h-8 text-[11px]"
                                data-testid="ask-drawer-done-btn"
                            >
                                Close
                            </Button>
                        </div>
                    ) : (
                        <>
                            <textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="What would you like to ask? E.g. 'Can you clarify the aligner sequencing in this workflow for a Class II case?'"
                                className="flex-1 w-full text-sm bg-secondary/30 border border-border rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                                maxLength={2000}
                                data-testid="ask-drawer-textarea"
                            />
                            <div className="mt-2 text-[10px] text-muted-foreground">
                                {question.length}/2000 · shared with the Invisique CoE Support team
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <Button
                                    onClick={submit}
                                    disabled={submitting}
                                    size="sm"
                                    className="h-9 flex-1 text-xs font-bold"
                                    data-testid="ask-drawer-submit-btn"
                                >
                                    <ChatCircleDots size={13} weight="fill" className="mr-1.5" />
                                    {submitting ? "Sending…" : "Send question"}
                                </Button>
                                <Button
                                    onClick={onClose}
                                    size="sm"
                                    variant="outline"
                                    className="h-9 text-xs"
                                    data-testid="ask-drawer-cancel-btn"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}


/* ═════════════════════════════════════════════════════════════════════
   Helpers
   ═════════════════════════════════════════════════════════════════════ */

function computeStreak(lessonProgress) {
    const days = new Set();
    Object.values(lessonProgress || {}).forEach((p) => {
        const ts = p?.last_viewed_at || p?.viewed_at;
        if (!ts) return;
        try {
            const d = new Date(ts);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            days.add(key);
        } catch { /* ignore */ }
    });
    if (!days.size) return 0;
    // Walk back from today looking for consecutive-day hits.
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 365; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        if (days.has(key)) streak += 1;
        else if (i > 0) break;   // gap → stop (allow zero-hit today; started later)
    }
    return streak;
}


function formatEta(minutes) {
    if (!minutes) return "0m";
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}


function printTranscript(lesson) {
    try {
        const w = window.open("", "_blank", "width=800,height=900");
        if (!w) { toast.error("Popup blocked — allow popups to print."); return; }
        w.document.write(`<!doctype html><html><head><title>${escapeHtml(lesson.title)}</title>
            <style>body{font-family:Georgia,serif;max-width:720px;margin:32px auto;padding:0 24px;color:#1a2b40;line-height:1.7} h1{font-size:24pt;margin-bottom:8px} .meta{color:#5f6c7b;font-size:11pt;margin-bottom:24px;padding-bottom:12px;border-bottom:1px solid #b48a3e} h2,h3{color:#1a2b40}</style>
            </head><body>
            <h1>${escapeHtml(lesson.title)}</h1>
            <div class="meta">Invisique&trade; Academy · Transcript</div>
            ${lesson.text_body || ""}
        </body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 350);
    } catch (e) { toast.error("Print failed."); }
}


function escapeHtml(s) {
    return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}


/* ═════════════════════════════════════════════════════════════════════
   PaywallGate — the "you haven't purchased this paid course yet" screen.
   Renders instead of the player when GET /my-progress returns
   `payment_required: true`. Auto-opens PaymentPendingDialog for the CTA.
   ═════════════════════════════════════════════════════════════════════ */

function PaywallGate({ course, priceRupees }) {
    const [dlgOpen, setDlgOpen] = useState(true);
    return (
        <div className="min-h-screen bg-secondary/30 grid place-items-center px-6 py-12" data-testid="course-paywall-gate">
            <div className="max-w-lg w-full bg-card border border-border rounded-2xl p-8 shadow-lg text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mx-auto mb-4 grid place-items-center text-primary">
                    <Books size={26} weight="duotone" />
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">Paid Course</div>
                <h1 className="font-display text-2xl font-bold mt-1 tracking-tight" data-testid="course-paywall-title">
                    {course.title}
                </h1>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Enroll to unlock every module, quiz, and the certificate.
                </p>
                <div className="mt-5 inline-flex items-baseline gap-1.5 justify-center">
                    <span className="font-display text-4xl font-black text-primary tabular-nums">
                        ₹{priceRupees.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">· One-time · Lifetime access</span>
                </div>
                <div className="mt-6">
                    <Button
                        size="lg"
                        className="w-full h-11 font-semibold"
                        onClick={() => setDlgOpen(true)}
                        data-testid="course-paywall-enroll-btn"
                    >
                        Enroll via CoE Support
                    </Button>
                </div>
                <Link
                    to="/doctor/academy"
                    className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline"
                    data-testid="course-paywall-back-link"
                >
                    <ArrowLeft size={11} weight="bold" /> Back to Invisique Academy
                </Link>
            </div>
            <PaymentPendingDialog
                open={dlgOpen}
                onOpenChange={setDlgOpen}
                courseTitle={course.title}
                priceRupees={priceRupees}
            />
        </div>
    );
}



/* ═════════════════════════════════════════════════════════════════════
   iter100.156 — Case Submission
   Sidebar entry + drawer for courses that require an admin-approved
   clinical case before certificate issuance.
   ═════════════════════════════════════════════════════════════════════ */

function CaseSubmissionNav({ courseId, progress, onProgressUpdate }) {
    const summary = progress?.summary;
    const status = summary?.case_status || "none";
    const [open, setOpen] = useState(false);

    const tone = useMemo(() => {
        if (status === "approved") return "bg-emerald-50 text-emerald-800";
        if (status === "pending")  return "bg-amber-50 text-amber-800";
        if (status === "rejected") return "bg-rose-50 text-rose-800";
        return "bg-secondary text-foreground";
    }, [status]);

    const subLabel = useMemo(() => {
        if (status === "approved") return "Approved ✓ · certificate unlocked";
        if (status === "pending")  return "Awaiting admin review…";
        if (status === "rejected") return "Rejected — revise & resubmit";
        return "Required to earn your certificate";
    }, [status]);

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid="course-view-case-submission-nav">
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${tone}`}
                data-testid="case-submission-toggle"
            >
                <div className="w-6 h-6 rounded-md bg-white/70 grid place-items-center shrink-0">
                    <FileText size={11} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold">Case Submission</div>
                    <div className="text-[10px]">{subLabel}</div>
                </div>
                {status === "approved" && <CheckCircle size={11} weight="fill" className="text-emerald-500" />}
            </button>
            {open && (
                <CaseSubmissionDrawer
                    courseId={courseId}
                    onClose={() => setOpen(false)}
                    onSaved={onProgressUpdate}
                />
            )}
        </div>
    );
}


function CaseSubmissionDrawer({ courseId, onClose, onSaved }) {
    const [submission, setSubmission] = useState(null);
    const [courseNotes, setCourseNotes] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({
        patient_summary: "",
        treatment_summary: "",
        outcome_summary: "",
        complications: "",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/academy/course-cases/courses/${courseId}/mine`);
            setSubmission(data.submission || null);
            setCourseNotes(data.course_notes || "");
            if (data.submission) {
                setForm({
                    patient_summary: data.submission.patient_summary || "",
                    treatment_summary: data.submission.treatment_summary || "",
                    outcome_summary: data.submission.outcome_summary || "",
                    complications: data.submission.complications || "",
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load case submission.");
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => { load(); }, [load]);

    const isApproved = submission?.status === "approved";
    const isPending = submission?.status === "pending";
    const rejected = submission?.status === "rejected";
    const attachments = submission?.attachments || [];

    const save = useCallback(async () => {
        for (const key of ["patient_summary", "treatment_summary", "outcome_summary"]) {
            if ((form[key] || "").trim().length < 20) {
                toast.error(`${key.replace(/_/g, " ")} must be at least 20 characters.`);
                return;
            }
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/academy/course-cases/courses/${courseId}/submit`, form);
            setSubmission(data.submission);
            toast.success(isPending || rejected
                ? "Case updated — resubmitted for review."
                : "Case submitted! Awaiting admin review.");
            await onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save submission.");
        } finally {
            setSaving(false);
        }
    }, [courseId, form, isPending, rejected, onSaved]);

    const upload = useCallback(async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await api.post(`/academy/course-cases/courses/${courseId}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Attachment uploaded.");
            await load();
            await onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't upload attachment.");
        } finally {
            setUploading(false);
        }
    }, [courseId, load, onSaved]);

    const remove = useCallback(async (attId) => {
        try {
            await api.delete(`/academy/course-cases/courses/${courseId}/attachments/${attId}`);
            toast.success("Attachment removed.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete attachment.");
        }
    }, [courseId, load]);

    return (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex justify-end" data-testid="case-submission-drawer" onClick={onClose}>
            <div className="w-full max-w-2xl h-full bg-card shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 z-10">
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Certification requirement</div>
                        <h2 className="font-display text-xl font-bold mt-0.5">Case Submission</h2>
                        {submission && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Status: <span className={`font-bold uppercase ${isApproved ? "text-emerald-700" : isPending ? "text-amber-700" : "text-rose-700"}`}>{submission.status}</span>
                            </div>
                        )}
                    </div>
                    <button type="button" onClick={onClose} className="text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="case-drawer-close">
                        Close
                    </button>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-muted-foreground">Loading…</div>
                ) : (
                    <div className="p-6 space-y-5">
                        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary">How this works</div>
                            <p className="mt-1.5 leading-relaxed text-foreground/80">
                                This course requires you to submit one completed clinical case for admin review. Once approved, your certificate is issued automatically (assuming lessons, quizzes, and any final exam are also complete). Keep patient details fully anonymised.
                            </p>
                        </div>

                        {courseNotes && (
                            <div className="rounded-lg border border-border bg-secondary/40 p-4">
                                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Author&apos;s brief</div>
                                <RichTextView html={courseNotes} className="mt-1.5" />
                            </div>
                        )}

                        {rejected && submission?.admin_notes && (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-rose-800">Admin feedback</div>
                                <div className="text-sm mt-1.5 whitespace-pre-wrap text-rose-900">{submission.admin_notes}</div>
                            </div>
                        )}

                        {isApproved && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                                <div className="font-bold flex items-center gap-1.5">
                                    <CheckCircle size={14} weight="fill" /> Approved by {submission.reviewed_by_name || "Admin"}
                                </div>
                                {submission.admin_notes && (
                                    <p className="mt-1.5 whitespace-pre-wrap">{submission.admin_notes}</p>
                                )}
                            </div>
                        )}

                        <FormField
                            label="Patient summary"
                            hint="Age, gender (anonymised), chief complaint, baseline findings."
                            value={form.patient_summary}
                            onChange={(v) => setForm((f) => ({ ...f, patient_summary: v }))}
                            disabled={isApproved || saving}
                            testid="case-form-patient"
                        />
                        <FormField
                            label="Treatment summary"
                            hint="Plan, tray count, IPR / attachments / mid-course refinements, protocol deviations."
                            value={form.treatment_summary}
                            onChange={(v) => setForm((f) => ({ ...f, treatment_summary: v }))}
                            disabled={isApproved || saving}
                            testid="case-form-treatment"
                        />
                        <FormField
                            label="Outcome summary"
                            hint="Final result vs. plan, patient satisfaction, retention protocol."
                            value={form.outcome_summary}
                            onChange={(v) => setForm((f) => ({ ...f, outcome_summary: v }))}
                            disabled={isApproved || saving}
                            testid="case-form-outcome"
                        />
                        <FormField
                            label="Complications (optional)"
                            hint="Anything unexpected — refinements, non-compliance, escalations."
                            value={form.complications}
                            onChange={(v) => setForm((f) => ({ ...f, complications: v }))}
                            disabled={isApproved || saving}
                            testid="case-form-complications"
                            minChars={0}
                        />

                        {!isApproved && (
                            <div className="flex items-center justify-end gap-2">
                                <Button onClick={save} disabled={saving} data-testid="case-form-submit">
                                    {saving ? "Saving…" : (submission ? "Update & resubmit" : "Submit for review")}
                                </Button>
                            </div>
                        )}

                        {/* Attachments */}
                        <div className="pt-4 border-t border-border">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Attachments</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">JPG / PNG / PDF / MP4 · up to 25 MB · max 8 files.</div>
                                </div>
                                {!isApproved && submission && (
                                    <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-primary/40 bg-primary/10 text-primary text-xs font-bold cursor-pointer hover:bg-primary/20 transition-colors" data-testid="case-attachment-upload-trigger">
                                        <UploadSimple size={13} weight="bold" />
                                        {uploading ? "Uploading…" : "Add file"}
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/quicktime,video/webm"
                                            onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                if (f) upload(f);
                                                e.target.value = "";
                                            }}
                                            data-testid="case-attachment-upload-input"
                                        />
                                    </label>
                                )}
                            </div>

                            {attachments.length === 0 ? (
                                <div className="mt-3 py-6 text-center text-sm text-muted-foreground italic border border-dashed border-border rounded-lg">
                                    {submission ? "No attachments yet." : "Save your write-up first, then attach supporting files."}
                                </div>
                            ) : (
                                <div className="mt-3 space-y-2">
                                    {attachments.map((a) => (
                                        <div
                                            key={a.id}
                                            className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/40"
                                            data-testid={`case-attachment-row-${a.id}`}
                                        >
                                            <Paperclip size={14} weight="duotone" className="text-primary" />
                                            <a
                                                href={`${API}/academy/course-cases/attachments/${a.id}/download`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm font-semibold text-foreground hover:underline truncate flex-1"
                                            >
                                                {a.filename}
                                            </a>
                                            <span className="text-[10px] uppercase text-muted-foreground tabular-nums">
                                                {(a.size_bytes / 1024).toFixed(0)} KB
                                            </span>
                                            {!isApproved && (
                                                <button
                                                    type="button"
                                                    onClick={() => remove(a.id)}
                                                    className="h-7 w-7 grid place-items-center rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                                                    data-testid={`case-attachment-delete-${a.id}`}
                                                    title="Remove"
                                                >
                                                    <Trash size={11} weight="bold" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function FormField({ label, hint, value, onChange, disabled, testid, minChars = 20 }) {
    const remaining = Math.max(0, minChars - (value || "").trim().length);
    return (
        <label className="block">
            <div className="flex items-baseline justify-between mb-1">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{label}</div>
                {minChars > 0 && (
                    <div className={`text-[10px] tabular-nums ${remaining > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {remaining > 0 ? `${remaining} more chars needed` : "Ready"}
                    </div>
                )}
            </div>
            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={hint}
                rows={4}
                className="text-sm"
                data-testid={testid}
            />
        </label>
    );
}
